import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../api/supabase'
import { useAuth } from '../contexts/AuthContext'

interface DuplicateJobModalProps {
  sourceJobId: string
  onClose: () => void
}

// Customer-relationship FKs on `jobs` that make sense to carry over for repeat business with
// the same customer/route -- only the ones actually set on the source job are shown as
// checkboxes (see CUSTOMER_FK_FIELDS.filter below).
const CUSTOMER_FK_FIELDS: { key: string; label: string }[] = [
  { key: 'consignor_id', label: 'Consignor' },
  { key: 'importer_id', label: 'Importer' },
  { key: 'overseas_agent_id', label: 'Overseas Agent' },
  { key: 'notify_id', label: 'Notify' },
  { key: 'invoice_to_id', label: 'Customer' },
  { key: 'ovs_house_agent_id', label: 'Overseas House Agent' },
  { key: 'break_bulk_agent_id', label: 'Break Bulk Agent' },
  { key: 'port_agent_id', label: 'Port Agent' },
  { key: 'hbl_consignor_id', label: 'HBL Consignor' },
  { key: 'hbl_importer_id', label: 'HBL Importer' },
  { key: 'hbl_notify_id', label: 'HBL Notify' },
]

interface SourceJob {
  id: string
  job_ref: string
  direction: 'IMP' | 'EXP'
  transport_mode: string
  job_type: string
  department_id: string | null
  branch_code: string | null
  incoterm_code: string | null
  booked_date: string | null
  [key: string]: any // customer FK columns, indexed dynamically via CUSTOMER_FK_FIELDS
}

interface SeaAir {
  shipping_line_id?: string | null
  shipping_terms_id?: string | null
  airline_id?: string | null
}

export default function DuplicateJobModal({ sourceJobId, onClose }: DuplicateJobModalProps) {
  const navigate = useNavigate()
  const { userEmail } = useAuth()

  const [loading, setLoading] = useState(true)
  const [job, setJob] = useState<SourceJob | null>(null)
  const [seaAir, setSeaAir] = useState<SeaAir | null>(null)
  const [customerNames, setCustomerNames] = useState<Record<string, string>>({})
  const [containerTypes, setContainerTypes] = useState<{ id: string; label: string }[]>([]) // one entry per source container
  const [goodsDescription, setGoodsDescription] = useState<string | null>(null)

  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [copyContainerType, setCopyContainerType] = useState(false)
  const [copyCommodity, setCopyCommodity] = useState(false)

  const [previewRef, setPreviewRef] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      const { data: jobRow, error: jobErr } = await supabase.from('jobs')
        .select(`
          id, job_ref, direction, transport_mode, job_type, department_id, branch_code,
          incoterm_code, booked_date,
          importer_id, consignor_id, overseas_agent_id, notify_id, invoice_to_id,
          ovs_house_agent_id, break_bulk_agent_id, port_agent_id,
          hbl_importer_id, hbl_consignor_id, hbl_notify_id
        `)
        .eq('id', sourceJobId).single()
      if (jobErr || !jobRow) { if (!cancelled) setError(jobErr?.message || 'Failed to load source job'); setLoading(false); return }

      const [{ data: seaRows }, { data: airRows }, { data: containerRows }, { data: goodsRows }] = await Promise.all([
        supabase.from('job_sea_details').select('shipping_line_id, shipping_terms_id').eq('job_id', sourceJobId).maybeSingle(),
        supabase.from('job_air_details').select('airline_id, shipping_terms_id').eq('job_id', sourceJobId).maybeSingle(),
        supabase.from('job_containers').select('container_type_id, container_types(code)').eq('job_id', sourceJobId),
        supabase.from('job_goods').select('goods_description').eq('job_id', sourceJobId).limit(1).maybeSingle(),
      ])

      const custIds = [...new Set(CUSTOMER_FK_FIELDS.map(f => (jobRow as any)[f.key]).filter(Boolean))]
      const names: Record<string, string> = {}
      if (custIds.length) {
        const { data: custs } = await supabase.from('customers').select('id, name').in('id', custIds)
        for (const c of (custs as { id: string; name: string }[] | null) || []) names[c.id] = c.name
      }

      if (cancelled) return
      setJob(jobRow as SourceJob)
      setSeaAir({ ...(seaRows || {}), ...(airRows || {}) })
      setCustomerNames(names)
      setContainerTypes(((containerRows as any[] | null) || []).filter(c => c.container_type_id).map(c => ({ id: c.container_type_id, label: c.container_types?.code || c.container_type_id })))
      setGoodsDescription((goodsRows as { goods_description: string | null } | null)?.goods_description || null)

      // Default-checked: customer FKs actually set + incoterm + route fields.
      const initial: Record<string, boolean> = {}
      for (const f of CUSTOMER_FK_FIELDS) if ((jobRow as any)[f.key]) initial[f.key] = true
      if ((jobRow as any).incoterm_code) initial.incoterm_code = true
      if (seaRows?.shipping_line_id) initial.shipping_line_id = true
      if (seaRows?.shipping_terms_id || airRows?.shipping_terms_id) initial.shipping_terms_id = true
      if (airRows?.airline_id) initial.airline_id = true
      setChecked(initial)

      const { data: refPreview } = await supabase.rpc('preview_next_job_ref', { p_direction: jobRow.direction })
      if (!cancelled) setPreviewRef(refPreview as string)

      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [sourceJobId])

  function toggle(key: string) {
    setChecked(prev => ({ ...prev, [key]: !prev[key] }))
  }

  async function handleCreate() {
    if (!job) return
    setError(null)
    setCreating(true)
    try {
      // Z36 redo: no reservation row is written for a copy. The number is taken by the jobs insert itself
      // (jobs.job_ref is unique); if someone else took it a moment ago, ask for the next one and try again.
      const { data: firstRef, error: previewErr } = await supabase.rpc('preview_next_job_ref', { p_direction: job.direction })
      if (previewErr) throw previewErr
      let freshRef = firstRef as string

      const jobInsert: Record<string, any> = {
        job_ref: freshRef,
        direction: job.direction,
        transport_mode: job.transport_mode,
        job_type: 'SINGLE',
        department_id: job.department_id,
        branch_code: job.branch_code,
        created_by: userEmail,
        copied_from_job_id: sourceJobId,
      }
      for (const f of CUSTOMER_FK_FIELDS) if (checked[f.key]) jobInsert[f.key] = job[f.key]
      if (checked.incoterm_code) jobInsert.incoterm_code = job.incoterm_code

      let { data: newJob, error: insertErr } = await supabase.from('jobs').insert(jobInsert).select('id, job_ref').single()
      if (insertErr?.code === '23505') {
        const { data: nextRef, error: nextErr } = await supabase.rpc('preview_next_job_ref', { p_direction: job.direction })
        if (nextErr) throw nextErr
        freshRef = nextRef as string
        jobInsert.job_ref = freshRef
        ;({ data: newJob, error: insertErr } = await supabase.from('jobs').insert(jobInsert).select('id, job_ref').single())
      }
      if (insertErr || !newJob) throw insertErr ?? new Error('Job was not created')

      if (job.transport_mode === 'SEA' && (checked.shipping_line_id || checked.shipping_terms_id)) {
        await supabase.from('job_sea_details').insert({
          job_id: newJob.id,
          shipping_line_id: checked.shipping_line_id ? seaAir?.shipping_line_id : null,
          shipping_terms_id: checked.shipping_terms_id ? seaAir?.shipping_terms_id : null,
        })
      }
      if (job.transport_mode === 'AIR' && (checked.airline_id || checked.shipping_terms_id)) {
        await supabase.from('job_air_details').insert({
          job_id: newJob.id,
          airline_id: checked.airline_id ? seaAir?.airline_id : null,
          shipping_terms_id: checked.shipping_terms_id ? seaAir?.shipping_terms_id : null,
        })
      }
      if (copyContainerType && containerTypes.length) {
        await supabase.from('job_containers').insert(
          containerTypes.map(ct => ({ job_id: newJob.id, container_type_id: ct.id }))
        )
      }
      if (copyCommodity && goodsDescription) {
        await supabase.from('job_goods').insert({ job_id: newJob.id, goods_description: goodsDescription })
      }

      navigate(`/jobs/${newJob.id}`, { state: { copiedFrom: job.job_ref } })
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to create copy')
    } finally {
      setCreating(false)
    }
  }

  const routeLabel = job?.transport_mode === 'SEA' ? 'Sea' : job?.transport_mode === 'AIR' ? 'Air' : job?.transport_mode

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white">Duplicate Job</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {loading ? (
          <div className="px-6 py-10 text-center text-gray-400">Loading…</div>
        ) : !job ? (
          <div className="px-6 py-6 text-sm text-red-600">{error || 'Job not found'}</div>
        ) : job.job_type === 'CONSOL' ? (
          <div className="px-6 py-6 text-sm text-gray-600 dark:text-gray-300">
            This job is a Consolidation (CONSOL) — Duplicate isn't supported for consol jobs in Phase 1.
            Use AutoJobs to create a new consol batch instead.
          </div>
        ) : (
          <>
            <div className="px-6 py-4 space-y-4">
              <div className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 rounded-lg px-3 py-2">
                Source: <span className="font-mono font-semibold">{job.job_ref}</span>
                {job.booked_date && <> · Booked {job.booked_date}</>}
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400">
                Always copied: {job.direction === 'IMP' ? 'Import' : 'Export'} · {routeLabel} · Single{job.branch_code ? ` · ${job.branch_code}` : ''}
              </div>

              {CUSTOMER_FK_FIELDS.some(f => job[f.key]) || job.incoterm_code || seaAir?.shipping_line_id || seaAir?.airline_id || seaAir?.shipping_terms_id ? (
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">✅ Customer &amp; route</h4>
                  <div className="space-y-1.5">
                    {CUSTOMER_FK_FIELDS.filter(f => job[f.key]).map(f => (
                      <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={!!checked[f.key]} onChange={() => toggle(f.key)} />
                        {f.label}: <span className="font-medium">{customerNames[job[f.key]] || job[f.key]}</span>
                      </label>
                    ))}
                    {job.incoterm_code && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={!!checked.incoterm_code} onChange={() => toggle('incoterm_code')} />
                        Incoterm: <span className="font-medium">{job.incoterm_code}</span>
                      </label>
                    )}
                    {seaAir?.shipping_line_id && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={!!checked.shipping_line_id} onChange={() => toggle('shipping_line_id')} />
                        Shipping Line
                      </label>
                    )}
                    {seaAir?.airline_id && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={!!checked.airline_id} onChange={() => toggle('airline_id')} />
                        Airline
                      </label>
                    )}
                    {seaAir?.shipping_terms_id && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={!!checked.shipping_terms_id} onChange={() => toggle('shipping_terms_id')} />
                        Freight Terms
                      </label>
                    )}
                  </div>
                </div>
              ) : null}

              {(containerTypes.length > 0 || goodsDescription) && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">⚙️ Optional</h4>
                  <div className="space-y-1.5">
                    {containerTypes.length > 0 && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={copyContainerType} onChange={() => setCopyContainerType(v => !v)} />
                        Container type: {containerTypes.map(c => c.label).join(', ')} (×{containerTypes.length} blank container{containerTypes.length > 1 ? 's' : ''} will be added)
                      </label>
                    )}
                    {goodsDescription && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={copyCommodity} onChange={() => setCopyCommodity(v => !v)} />
                        Commodity description seed: "{goodsDescription}"
                      </label>
                    )}
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-400">
                ❌ Never copied: job ref, BL/AWB/container/vessel numbers, dates, customer PO, goods quantities, notes
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            <div className="px-6 pb-5 pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                New job will use: <span className="font-mono font-semibold text-gray-700 dark:text-gray-200">{previewRef || '…'}</span> (preview only — not reserved yet)
              </p>
              <div className="flex gap-3 justify-end">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
                <button onClick={handleCreate} disabled={creating || !previewRef}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {creating ? 'Creating…' : 'Create Copy'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
